import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { Box, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { updateEntity } from '../../services/api';
import { useTranslationContext } from '../../context/TranslationContext';
import SearchLocationInput, { LocationResult } from './SearchLocationInput';

type AddressDraft = {
  street: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
};

type Props = {
  baseUrl?: string;
  spaceId?: string;
  entityType: 'contact' | 'business';
  entityId: string;
  onBack: () => void;
  onEntityUpdated?: (entity: unknown) => void;
};

const EMPTY_DRAFT: AddressDraft = {
  street: '',
  city: '',
  state: '',
  zipcode: '',
  country: '',
};

const SearchLocationEditor = ({
  baseUrl,
  spaceId,
  entityType,
  entityId,
  onBack,
  onEntityUpdated,
}: Props) => {
  const t = useTranslationContext();

  const [draft, setDraft] = useState<AddressDraft>(EMPTY_DRAFT);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');

  const [isSavingMain, setIsSavingMain] = useState(false);
  const [isSavingSecondary, setIsSavingSecondary] = useState(false);

  const canSubmit = useMemo(() => {
    return Boolean(
      String(draft.street ?? '').trim() ||
      String(draft.city ?? '').trim() ||
      String(draft.state ?? '').trim() ||
      String(draft.zipcode ?? '').trim() ||
      String(draft.country ?? '').trim(),
    );
  }, [draft]);

  const handlePlaceSelected = (result: LocationResult) => {
    setQuery(result.address ?? '');

    const computedStreet =
      String(result.shortAddress ?? '').trim() ||
      [result.streetName, result.streetNumber].filter(Boolean).join(' ').trim();

    setDraft({
      street: computedStreet,
      city: result.city ?? '',
      state: result.state ?? '',
      zipcode: result.postalCode ?? '',
      country: result.country ?? '',
    });

    setError('');
  };

  const handleError = (errorMessage: string) => {
    setError(String(errorMessage ?? ''));
  };

  const handleChange = (key: keyof AddressDraft) => (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setDraft((prev) => ({ ...prev, [key]: next }));
  };

  const handleSaveMainAddress = async () => {
    if (isSavingMain) return;
    if (!baseUrl || !spaceId) return;
    if (!entityId) return;

    setIsSavingMain(true);
    try {
      const updated = await updateEntity({
        baseUrl,
        spaceId,
        entityType,
        entityId,
        body: {
          address1: {
            street: String(draft.street ?? ''),
            city: String(draft.city ?? ''),
            state: String(draft.state ?? ''),
            zipcode: String(draft.zipcode ?? ''),
            country: String(draft.country ?? ''),
          },
        },
      });

      onEntityUpdated?.(updated);
      onBack();
    } finally {
      setIsSavingMain(false);
    }
  };

  const handleSaveSecondaryAddress = async () => {
    if (isSavingSecondary) return;
    if (!baseUrl || !spaceId) return;
    if (!entityId) return;

    setIsSavingSecondary(true);
    try {
      const updated = await updateEntity({
        baseUrl,
        spaceId,
        entityType,
        entityId,
        body: {
          address2: {
            street: String(draft.street ?? ''),
            city: String(draft.city ?? ''),
            state: String(draft.state ?? ''),
            zipcode: String(draft.zipcode ?? ''),
            country: String(draft.country ?? ''),
          },
        },
      });

      onEntityUpdated?.(updated);
      onBack();
    } finally {
      setIsSavingSecondary(false);
    }
  };

  const isDisabled = !canSubmit || isSavingMain || isSavingSecondary;

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton size="small" onClick={onBack} aria-label={t('global.back')}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="h6">{t('searchLocationModal.searchAddress')}</Typography>
      </Stack>

      <Stack spacing={3}>
        <Box sx={{ position: 'relative', zIndex: 1400 }}>
          <SearchLocationInput
            onPlaceSelected={handlePlaceSelected}
            onError={handleError}
            placeholder={t('searchLocationModal.enterLocation')}
          />

          {error ? (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {error}
            </Typography>
          ) : null}
        </Box>

        <TextField
          label={t('conversations.contactDetails.street')}
          value={draft.street}
          onChange={handleChange('street')}
          fullWidth
          size="medium"
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('searchLocationModal.city')}
            value={draft.city}
            onChange={handleChange('city')}
            fullWidth
            size="medium"
          />
          <TextField
            label={t('searchLocationModal.state')}
            value={draft.state}
            onChange={handleChange('state')}
            fullWidth
            size="medium"
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('searchLocationModal.postalCode')}
            value={draft.zipcode}
            onChange={handleChange('zipcode')}
            fullWidth
            size="medium"
          />
          <TextField
            label={t('searchLocationModal.country')}
            value={draft.country}
            onChange={handleChange('country')}
            fullWidth
            size="medium"
          />
        </Stack>

        {query ? (
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {query}
          </Typography>
        ) : null}

        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
          <LoadingButton
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSaveMainAddress}
            loading={isSavingMain}
            disabled={isDisabled}
          >
            {t('searchLocationModal.saveMainAddress')}
          </LoadingButton>

          <LoadingButton
            variant="outlined"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSaveSecondaryAddress}
            loading={isSavingSecondary}
            disabled={isDisabled}
          >
            {t('searchLocationModal.saveSecondaryAddress')}
          </LoadingButton>
        </Stack>
      </Stack>
    </Box>
  );
};

export default SearchLocationEditor;
